import { Editor, Element, type Text, Transforms } from 'slate'

const LIST_TYPES = ['numbered-list', 'bulleted-list']

export function toggleBlock(editor: Editor, format: Element['type']) {
  const isList = LIST_TYPES.includes(format)
  const isActive = isList
    ? isListActive(editor, format)
    : isBlockActive(editor, format)

  Editor.withoutNormalizing(editor, () => {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        LIST_TYPES.includes(n.type),
      split: true,
      mode: 'lowest',
    })

    let newProperties: Partial<Element>

    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }

    Transforms.setNodes<Element>(editor, newProperties)

    if (!isActive && isList) {
      const block = { type: format, children: [] } as Element
      Transforms.wrapNodes(editor, block)
    }
  })
}

export function toggleMark(editor: Editor, format: keyof Omit<Text, 'text'>) {
  const isActive = isMarkActive(editor, format)

  if (isActive) {
    return Editor.removeMark(editor, format)
  } else {
    return Editor.addMark(editor, format, true)
  }
}

export function isBlockActive(editor: Editor, format: Element['type']) {
  const { selection } = editor
  if (!selection) return false

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) && Element.isElement(n) && n.type === format,
    }),
  )

  return !!match
}

export function isListActive(editor: Editor, format: Element['type']) {
  const { selection } = editor
  if (!selection) return false

  const [match] = Editor.nodes(editor, {
    at: Editor.unhangRange(editor, selection),
    match: (n) =>
      !Editor.isEditor(n) &&
      Element.isElement(n) &&
      LIST_TYPES.includes(n.type),
    mode: 'lowest',
  })

  return match && (match[0] as any).type === format
}

export function selectedBlockTypes(editor: Editor) {
  const { selection } = editor
  if (!selection) return

  const matches = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    }),
  )

  return matches.map(([element]) => (element as Element).type)
}

export function isMarkActive(editor: Editor, format: keyof Omit<Text, 'text'>) {
  try {
    const marks = Editor.marks(editor)
    return marks ? marks[format] === true : false
  } catch {
    return false
  }
}
